import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Account.scss";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckIcon from "@mui/icons-material/Check";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "Profile");
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const getHeaderTitle = () => {
    if (activeTab === "Security") return "Change Password";
    if (activeTab === "FAQ") return "Frequently Asked Questions";
    return activeTab;
  };

  const faqData = [
    {
      question: "How do I book a towing service?",
      answer: "Open the app, select your service type (e.g. Towing), enter your pickup and drop-off location, choose your vehicle, confirm the booking and proceed to payment."
    },
    {
      question: "Can I cancel my booking?",
      answer: "Yes, you can cancel your booking through the app. Please refer to our cancellation policy for any applicable fees."
    },
    {
      question: "How long will it take for a driver to arrive?",
      answer: "Arrival times vary based on your location and traffic, but typically a driver will arrive within 30-45 minutes."
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept all major credit cards, debit cards, and Apple/Google Pay."
    },
    {
      question: "How do I add or edit my vehicle details?",
      answer: "Go to your profile settings, select 'My Vehicles', and click 'Add Vehicle' or 'Edit' on an existing one."
    },
    {
      question: "What happens if my driver cancels?",
      answer: "If a driver cancels, we will immediately attempt to re-assign your request to another available driver nearby."
    },
    {
      question: "How are refunds processed?",
      answer: "Refunds are processed back to your original payment method and typically take 3-5 business days to appear."
    },
    {
      question: "How do I contact customer support?",
      answer: "You can reach us through the 'Support' tab in the app or email us at support@itsmywall.com."
    },
    {
      question: "Is my personal data safe?",
      answer: "Yes, we use industry-standard encryption and security measures to protect all your personal information."
    }
  ];

  return (
    <div className="account_page">
      {/* Sidebar */}
      <aside className="account_sidebar">
        <div className="account_sidebar__header">
          <h2>My Account</h2>
          <p>Manage your profile, security and get help from support</p>
        </div>

        <ul className="account_sidebar__menu">
          <li
            className={activeTab === "Profile" ? "active" : ""}
            onClick={() => setActiveTab("Profile")}
          >
            <div className="icon_wrapper">
              <PersonOutlineIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Profile</span>
              <span className="subtitle">View and edit your personal info</span>
            </div>
          </li>

          <li
            className={activeTab === "Security" ? "active" : ""}
            onClick={() => setActiveTab("Security")}
          >
            <div className="icon_wrapper">
              <SecurityIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Security</span>
              <span className="subtitle">Change your password.</span>
            </div>
          </li>

          <li
            className={activeTab === "Support" ? "active" : ""}
            onClick={() => setActiveTab("Support")}
          >
            <div className="icon_wrapper">
              <SupportAgentIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Support</span>
              <span className="subtitle">Get help and learn more</span>
            </div>
          </li>

          <li
            className={activeTab === "Terms and Conditions" ? "active" : ""}
            onClick={() => setActiveTab("Terms and Conditions")}
          >
            <div className="icon_wrapper">
              <DescriptionOutlinedIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Terms and Conditions</span>
              <span className="subtitle">company Terms and Conditions</span>
            </div>
          </li>

          <li
            className={activeTab === "Privacy Policy" ? "active" : ""}
            onClick={() => setActiveTab("Privacy Policy")}
          >
            <div className="icon_wrapper">
              <PrivacyTipOutlinedIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Privacy Policy</span>
              <span className="subtitle">Company Privacy Policy</span>
            </div>
          </li>

          <li
            className={activeTab === "FAQ" ? "active" : ""}
            onClick={() => setActiveTab("FAQ")}
          >
            <div className="icon_wrapper">
              <HelpOutlineIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">FAQ</span>
              <span className="subtitle">Frequently Asked Questions.</span>
            </div>
          </li>

          <li
            className={activeTab === "Logout" ? "active" : ""}
            onClick={() => navigate("/")}
          >
            <div className="icon_wrapper">
              <LogoutIcon />
            </div>
            <div className="text_wrapper">
              <span className="title">Logout</span>
              <span className="subtitle">You will be logout from platform</span>
            </div>
          </li>
        </ul>
      </aside>

      {/* Main Content */}
      <section className="account_content">
        <div className="account_content__header">
          <h2>{getHeaderTitle()}</h2>
        </div>

        {activeTab === "Profile" && (
          <div className="account_content__body">
            {/* Profile Image Section */}
            <div className="profile_image_section">
              <div className="image_wrapper">
                <img src="/static/images/avtar.svg" alt="Profile" />
                <div className="camera_badge">
                  <CameraAltIcon />
                </div>
              </div>
              <div className="image_info">
                JPG, PNG or GIF.
                <br />
                Max size 2MB.
              </div>
              <button className="btn_change_photo">
                <FileUploadOutlinedIcon /> Change Photo
              </button>
            </div>

            {/* Profile Form Section */}
            <div className="profile_form_section">
              <div className="form_row">
                <div className="form_group">
                  <label>First Name</label>
                  <div className="input_wrapper">
                    <span className="input_icon">
                      <PersonOutlineIcon />
                    </span>
                    <input type="text" defaultValue="Franklin" placeholder="Enter first name" />
                  </div>
                </div>
                <div className="form_group">
                  <label>Last Name</label>
                  <div className="input_wrapper">
                    <span className="input_icon">
                      <PersonOutlineIcon />
                    </span>
                    <input type="text" defaultValue="Jorb" placeholder="Enter last name" />
                  </div>
                </div>
              </div>

              <div className="form_group">
                <label>Phone Number</label>
                <div className="input_wrapper">
                  <span className="input_icon">
                    <LocalPhoneOutlinedIcon />
                  </span>
                  <input
                    type="tel"
                    defaultValue="+1 (555) 123-4567"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form_group">
                <label>Email Address</label>
                <div className="input_wrapper">
                  <span className="input_icon">
                    <MailOutlineIcon />
                  </span>
                  <input
                    type="email"
                    defaultValue="Franklinjorb145@gmail.com"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              <div className="form_actions">
                <button className="btn_save">
                  <CheckIcon /> Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Security" && (
          <div className="account_content__body">
            <div className="profile_form_section">
              <div className="form_group">
                <label>Current Password</label>
                <div className="input_wrapper">
                  <span className="input_icon">
                    <LockOutlinedIcon />
                  </span>
                  <input type="password" placeholder="Enter your password" />
                </div>
              </div>

              <div className="form_group">
                <label>New Password</label>
                <div className="input_wrapper">
                  <span className="input_icon">
                    <LockOutlinedIcon />
                  </span>
                  <input type="password" placeholder="Enter your password" />
                </div>
              </div>

              <div className="form_group">
                <label>Confirm Password</label>
                <div className="input_wrapper">
                  <span className="input_icon">
                    <LockOutlinedIcon />
                  </span>
                  <input type="password" placeholder="Enter your password" />
                </div>
              </div>

              <div className="form_actions">
                <button className="btn_save">
                  <CheckIcon /> Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Support" && (
          <div className="account_content__body" style={{ background: 'transparent', padding: 0 }}>
            <div className="support_cards_section">
              <div className="support_card">
                <div className="support_card__header">
                  <div className="icon_wrapper">
                    <LocalPhoneOutlinedIcon />
                  </div>
                  <div className="text_wrapper">
                    <h3>Phone Number</h3>
                    <p>Call us directly for immediate assistance.</p>
                  </div>
                </div>
                <div className="support_card__content">
                  +1 (415) 555-0123
                </div>
                <button className="btn_outline">
                  <LocalPhoneOutlinedIcon /> Call Now
                </button>
              </div>

              <div className="support_card">
                <div className="support_card__header">
                  <div className="icon_wrapper">
                    <MailOutlineIcon />
                  </div>
                  <div className="text_wrapper">
                    <h3>Email Address</h3>
                    <p>Send us an email and we'll respond within 24 hours.</p>
                  </div>
                </div>
                <div className="support_card__content">
                  support@itsmywall.com
                </div>
                <button className="btn_outline">
                  <MailOutlineIcon /> Send Email
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Terms and Conditions" && (
          <div className="account_content__body" style={{ background: 'transparent', padding: 0 }}>
            <div className="text_document_section">
              <p>Welcome to It's My Wall! By using our services, you agree to the following terms and conditions. Please read them carefully.</p>

              <div className="doc_block">
                <h3>Acceptance of Terms</h3>
                <p>By accessing our website and services, you acknowledge that you have read, understood, and agree to be bound by these terms.</p>
              </div>

              <div className="doc_block">
                <h3>User Responsibilities</h3>
                <p>You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.</p>
              </div>

              <div className="doc_block">
                <h3>Content Ownership</h3>
                <p>All content provided on our platform is owned by It's My Wall or our licensors. You may not reproduce, distribute, or create derivative works without permission.</p>
              </div>

              <div className="doc_block">
                <h3>Termination</h3>
                <p>We reserve the right to suspend or terminate your access to our services at any time, without notice, if you violate these terms.</p>
              </div>

              <div className="doc_block">
                <h3>Limitation of Liability</h3>
                <p>It's My Wall is not liable for any damages arising from your use of our services.</p>
              </div>

              <div className="doc_block">
                <h3>Changes to Terms</h3>
                <p>We may update these terms from time to time. Your continued use of our services after any changes indicates your acceptance of the new terms.</p>
              </div>

              <p style={{ marginTop: '10px' }}>For more detailed information, please refer to our full terms and conditions on our website.</p>
            </div>
          </div>
        )}

        {activeTab === "Privacy Policy" && (
          <div className="account_content__body" style={{ background: 'transparent', padding: 0 }}>
            <div className="text_document_section">
              <p>Welcome to It's My Wall! Your privacy is important to us. This policy outlines how we collect, use, and protect your information when you use our services.</p>

              <div className="doc_block">
                <h3>Information Collection</h3>
                <p>We collect personal information that you provide to us directly, such as your name, email address, and any other details you choose to share. We also gather data automatically, like your IP address and browsing behavior, to enhance your experience.</p>
              </div>

              <div className="doc_block">
                <h3>Use of Information</h3>
                <p>Your information helps us improve our services, communicate with you, and personalize your experience. We may also use your data for marketing purposes, but only with your consent.</p>
              </div>

              <div className="doc_block">
                <h3>Data Protection</h3>
                <p>We implement robust security measures to protect your information from unauthorized access, alteration, or destruction. However, no method of transmission over the internet is completely secure.</p>
              </div>

              <div className="doc_block">
                <h3>Sharing of Information</h3>
                <p>We do not sell or rent your personal information to third parties. We may share your data with trusted partners who assist us in operating our website and services, provided they agree to keep your information confidential.</p>
              </div>

              <div className="doc_block">
                <h3>Your Rights</h3>
                <p>You have the right to access, correct, or delete your personal information at any time. If you wish to exercise these rights, please contact us.</p>
              </div>

              <div className="doc_block">
                <h3>Changes to This Policy</h3>
                <p>We may update this privacy policy occasionally. We will notify you of any significant changes by posting the new policy on our website. Your continued use of our services after changes indicates your acceptance of the new policy.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "FAQ" && (
          <div className="account_content__body" style={{ background: 'transparent', padding: 0, flexDirection: 'column', gap: '25px' }}>
            <div className="faq_section">
              <div className="faq_search">
                <div className="search_icon">
                  <SearchIcon />
                </div>
                <input 
                  type="text" 
                  placeholder="Search for Question here......." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="faq_list">
                {faqData
                  .filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((faq, index) => {
                    // Use a unique index based on the original data for robust toggling, but since we are mapping a subset, let's just use the current index or question text.
                    const isExpanded = expandedFaq === index;
                    return (
                      <div className="faq_item" key={index}>
                        <div 
                          className={`faq_question ${isExpanded ? 'open' : ''}`}
                          onClick={() => setExpandedFaq(isExpanded ? null : index)}
                        >
                          {faq.question}
                          <span className="toggle_icon" style={{ pointerEvents: 'none' }}>
                            {isExpanded ? <RemoveIcon /> : <AddIcon />}
                          </span>
                        </div>
                        <div className={`faq_answer ${isExpanded ? 'open' : ''}`}>
                          {faq.answer}
                        </div>
                      </div>
                    );
                })}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Account;
